import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Profile } from '../../types';

interface AuthContextType {
    user: User | null;
    profile: Profile | null;
    session: Session | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<{ data?: any; error: any }>;
    signOut: () => Promise<void>;
    refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Max time (ms) to wait for auth initialization before unblocking the UI
const AUTH_TIMEOUT_MS = 8000;
// Max retries for fetching profile
const MAX_PROFILE_RETRIES = 2;
// Base delay for retry backoff (ms)
const RETRY_BASE_DELAY_MS = 1000;
// Mesmo usuário voltando à aba: o perfil só é rebuscado se a última busca tiver mais que isso.
const PERFIL_REVALIDAR_MS = 5 * 60 * 1000;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [loading, setLoading] = useState(true);
    
    // Guards against race conditions between getSession and onAuthStateChange
    const initializedRef = useRef(false);
    const fetchingProfileRef = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Quem está logado agora e quando o perfil veio pela última vez (ver onAuthStateChange).
    const userIdRef = useRef<string | null>(null);
    const perfilBuscadoEmRef = useRef(0);

    const finishLoading = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setLoading(false);
    }, []);

    const fetchProfile = useCallback(async (userId: string, retries = 0): Promise<void> => {
        // Prevent duplicate concurrent calls
        if (fetchingProfileRef.current) return;
        fetchingProfileRef.current = true;
        
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error) {
                console.error(`Error fetching profile (attempt ${retries + 1}):`, error);
                
                // Retry with backoff if we haven't exhausted retries
                if (retries < MAX_PROFILE_RETRIES) {
                    fetchingProfileRef.current = false;
                    const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retries);
                    await new Promise(resolve => setTimeout(resolve, delay));
                    return fetchProfile(userId, retries + 1);
                }
            } else {
                perfilBuscadoEmRef.current = Date.now();
                // Perfil igual ao que já está na tela: mantém o objeto, senão toda tela
                // que depende de `profile` recarrega à toa.
                setProfile(prev => (prev && JSON.stringify(prev) === JSON.stringify(data)) ? prev : data as Profile);
            }
        } catch (err) {
            console.error(`Unexpected error fetching profile (attempt ${retries + 1}):`, err);
            
            if (retries < MAX_PROFILE_RETRIES) {
                fetchingProfileRef.current = false;
                const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retries);
                await new Promise(resolve => setTimeout(resolve, delay));
                return fetchProfile(userId, retries + 1);
            }
        } finally {
            fetchingProfileRef.current = false;
            finishLoading();
        }
    }, [finishLoading]);

    useEffect(() => {
        // Safety timeout: unblock UI after AUTH_TIMEOUT_MS even if Supabase didn't respond
        timeoutRef.current = setTimeout(() => {
            if (loading) {
                console.warn(`[AuthContext] Auth initialization timed out after ${AUTH_TIMEOUT_MS}ms — unblocking UI`);
                setLoading(false);
            }
        }, AUTH_TIMEOUT_MS);

        // Check active session
        supabase.auth.getSession().then(({ data: { session } }) => {
            // Mark as initialized so onAuthStateChange doesn't duplicate work
            if (initializedRef.current) return;
            initializedRef.current = true;

            userIdRef.current = session?.user?.id ?? null;
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) {
                fetchProfile(session.user.id);
            } else {
                finishLoading();
            }
        }).catch((err) => {
            console.error('[AuthContext] getSession failed:', err);
            initializedRef.current = true;
            finishLoading();
        });

        // Listen for changes
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
            // Skip if this is the initial event and getSession already handled it
            if (!initializedRef.current) return;

            // O Supabase emite SIGNED_IN toda vez que o usuário volta para a aba (e
            // repassa o evento para as outras abas abertas) e TOKEN_REFRESHED a cada
            // hora. Tratar isso como login novo trocava o objeto `user` e rebuscava o
            // perfil, e cada volta à aba virava ~14 consultas por aba aberta. Em
            // 03/11/2026 isso era 23% de todas as requisições e entupia o banco.
            const novoUser = session?.user ?? null;
            const mesmoUsuario = !!novoUser && novoUser.id === userIdRef.current;
            userIdRef.current = novoUser?.id ?? null;

            setSession(prev => (prev && session && prev.access_token === session.access_token) ? prev : session);

            if (mesmoUsuario && event !== 'USER_UPDATED') {
                if (Date.now() - perfilBuscadoEmRef.current > PERFIL_REVALIDAR_MS) {
                    fetchProfile(novoUser.id);
                }
                return;
            }

            setUser(novoUser);
            if (novoUser) {
                fetchProfile(novoUser.id);
            } else {
                setProfile(null);
                finishLoading();
            }
        });

        return () => {
            subscription.unsubscribe();
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const signInWithPassword = async (email: string, password: string) => {
        // Restrição de domínio removida para suportar usuários de empresas parceiras
        // (ex: Gran). Contas são criadas exclusivamente por admins — não há auto-cadastro.
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        });

        return { data, error };
    };

    const signOut = async () => {
        try {
            await supabase.auth.signOut();
        } catch (err) {
            // Sessão já inválida no servidor (ex.: conta banida) — limpa local mesmo assim
            console.warn('[AuthContext] signOut failed, clearing local session anyway:', err);
        }
        setProfile(null);
        setUser(null);
        setSession(null);
    };

    const refreshProfile = async () => {
        if (user) await fetchProfile(user.id);
    };

    return (
        <AuthContext.Provider value={{
            user,
            profile,
            session,
            loading,
            signIn: signInWithPassword,
            signOut,
            refreshProfile
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
